import { useEffect, useRef, useState } from "react";
import { Maximize, Minimize, X } from "lucide-react";

import type { Video } from "@/model/videos";
import VideoMedia from "./video-media";
import VideoUpNextItem from "./video-up-next-item";
import IDENTIFIERS from "@/constants/test_id";

type VideoPlayerProps = {
  video: Video;
  videoList: Video[];
  onSelectVideo: (video: Video) => void;
  onClose: () => void;
};

const ICON_BUTTON_CLASS =
  "flex size-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition-colors hover:bg-muted";

/**
 * YouTube-style watch layout inside the page: the player with its title and
 * actions on the left, and an "Other videos" column on the right (stacked
 * below the player on small screens).
 */
const VideoPlayer = ({
  video,
  videoList,
  onSelectVideo,
  onClose,
}: VideoPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const otherVideos = videoList.filter((item) => item !== video);

  // Bring the player into view when it opens or another video is picked
  useEffect(() => {
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [video]);

  // Keep the icon in sync when fullscreen is exited with Esc or the browser UI
  useEffect(() => {
    const syncFullscreenState = () =>
      setIsFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener("fullscreenchange", syncFullscreenState);
    return () =>
      document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      stageRef.current?.requestFullscreen();
    }
  };

  return (
    <div
      ref={containerRef}
      className="flex scroll-mt-4 flex-col gap-6 px-4 py-8 lg:flex-row"
    >
      {/* Player column */}
      <section className="min-w-0 flex-1">
        <div
          ref={stageRef}
          className="relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)]"
        >
          {/* key remounts the media so the loading state resets per video */}
          <VideoMedia key={video.link} video={video} />
        </div>

        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-base font-extrabold leading-snug">
              {video.topic}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {video.source} · {video.type}
            </p>
          </div>

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
              className={ICON_BUTTON_CLASS}
            >
              {isFullscreen ? (
                <Minimize className="size-4" />
              ) : (
                <Maximize className="size-4" />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close video"
              className={ICON_BUTTON_CLASS}
            >
              <X className="size-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Other videos column */}
      {otherVideos.length > 0 && (
        <aside className="w-full shrink-0 lg:w-[380px]">
          <h3 className="mb-3 text-base font-semibold">Other videos</h3>
          <div className="space-y-1">
            {otherVideos.map((item, index) => (
              <VideoUpNextItem
                key={`${item.link}-${index}`}
                video={item}
                onSelect={() => onSelectVideo(item)}
                testId={`${IDENTIFIERS.VIDEOS.VIDEO_ITEM}-${index}`}
              />
            ))}
          </div>
        </aside>
      )}
    </div>
  );
};

export default VideoPlayer;
